import React, { useContext, createContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Bell, Menu, X, Trash2, Workflow, BarChart3, Layers, FileText, Send, ScrollText, ShieldCheck, Trophy } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import NotificationBell from './NotificationBell';
import ActiveTasksBanner from './ActiveTasksBanner';
import ActiveTasksWelcomeModal from './ActiveTasksWelcomeModal';
import SidebarBadges from './SidebarBadges';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

// Create Notifications Context
export const NotificationsContext = createContext();

export default function Layout({ children }) {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [notifications, setNotifications] = React.useState([]);
  const [showNotifications, setShowNotifications] = React.useState(false);
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  React.useEffect(() => {
    fetchNotifications();
    
    // Her 30 saniyede bir yeni bildirimleri kontrol et
    const interval = setInterval(() => {
      fetchNotifications();
    }, 30000);
    
    // Service Worker'dan gelen NAVIGATE mesajlarını dinle (tüm sayfalar için)
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === 'NAVIGATE') {
        const taskId = event.data.taskId;
        const url = event.data.url;
        if (taskId) {
          navigate(`/tasks?openTask=${taskId}`);
        } else if (url) {
          navigate(url);
        }
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handleSWMessage);
    }
    
    return () => {
      clearInterval(interval);
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.removeEventListener('message', handleSWMessage);
      }
    };
  }, []);

  const fetchNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications`);
      setNotifications(response.data);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    }
  };

  const markAsRead = async (id) => {
    try {
      await axios.patch(`${API}/notifications/${id}/read`);
      fetchNotifications();
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  };

  const clearAllNotifications = async () => {
    try {
      // Okunmamış bildirimleri al
      const unreadNotifications = notifications.filter(n => !n.read);
      
      // UI'dan hemen tüm bildirimleri kaldır
      setNotifications([]);
      
      // Backend'e "okundu" işaretleme isteklerini gönder
      await Promise.all(
        unreadNotifications.map(notif => axios.patch(`${API}/notifications/${notif.id}/read`))
      );
      
      // fetchNotifications çağırmıyoruz - bildirimleri temiz tutuyoruz
      // Yeni bildirimler geldiğinde zaten useEffect ile güncellenecek
      
      toast.success('Tüm bildirimler temizlendi');
    } catch (error) {
      console.error('Failed to clear notifications:', error);
      toast.error('Bildirimler temizlenemedi');
      // Hata durumunda backend'den tekrar yükle
      fetchNotifications();
    }
  };

  const handleNotificationClick = async (notif) => {
    // Mark as read
    await markAsRead(notif.id);
    
    // Close dropdown
    setShowNotifications(false);
    
    // Close sidebar on mobile
    setSidebarOpen(false);
    
    // Navigate to task if it's a task notification
    if (notif.taskId) {
      // task_share dahil tüm görev bildirimleri → TaskBoard drawer ile aç
      navigate(`/tasks?openTask=${notif.taskId}`);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
    toast.success('Çıkış yapıldı');
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const menuItems = [
    { path: '/dashboard', label: 'Dashboard', iconComponent: 'barchart3', permission: 'can_view_dashboard' },
    { path: '/tasks', label: 'Görevler', iconComponent: 'layers', permission: 'can_comment' }, // Yorumcu görevleri görebilir
    { path: '/projects', label: 'Projeler', icon: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z', permission: 'can_manage_projects' }, // Sadece yöneticiler
    { path: '/teams', label: 'Takımlar', icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', permission: 'can_manage_teams' }, // Sadece yöneticiler
    { path: '/users', label: 'Kullanıcılar', icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z', permission: 'can_manage_users' },
    { path: '/workflows', label: 'İş Akışları', iconComponent: 'workflow', permission: 'can_manage_workflows' }, // Sadece yöneticiler
    { path: '/reports', label: 'Raporlar', icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', permission: 'can_view_reports' },
    { path: '/audit-log', label: 'Aktivite Günlüğü', iconComponent: 'scrolltext', permission: 'can_manage_users' },
    { path: '/files', label: 'Dosyalar', iconComponent: 'filetext', permission: 'can_download_files' }, // Yorumcu dosya indirebilir
    { path: '/notifications/history', label: 'Bildirim Geçmişi', iconComponent: 'barchart3', permission: 'can_comment', adminOnly: true }, // Sadece admin
    { path: '/badges/settings', label: 'Rozetler', iconComponent: 'trophy', adminOnly: true },
  ];

  // Role display names mapping
  const getRoleDisplayName = (role) => {
    const roleMap = {
      'admin': 'Admin',
      'proje_yoneticisi': 'Proje Yöneticisi',
      'project_manager': 'Proje Yöneticisi',
      'takim_lideri': 'Takım Lideri',
      'team_leader': 'Takım Lideri',
      'takim_uyesi': 'Takım Üyesi',
      'team_member': 'Takım Üyesi'
    };
    return roleMap[role?.toLowerCase()] || role;
  };

  // Filter menu items based on user permissions
  const visibleMenuItems = menuItems.filter(item => {
    // If adminOnly flag is set, only show to admin role
    if (item.adminOnly && user?.role !== 'admin') {
      return false;
    }
    
    // If no permission required, show to everyone
    if (!item.permission) return true;
    
    // Check if user has the required permission
    return user?.permissions?.includes(item.permission);
  });

  const notificationsValue = {
    notifications,
    showNotifications,
    setShowNotifications,
    unreadCount,
    handleNotificationClick,
    clearAllNotifications
  };

  return (
    <NotificationsContext.Provider value={notificationsValue}>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Fixed Notification Bell - Always Visible */}
      <div className="fixed top-4 right-4 z-50 lg:right-8">
        <NotificationBell />
      </div>
      {/* Mobile Header */}
      <header className="lg:hidden glass border-b border-gray-200 sticky top-0 z-40" data-testid="mobile-header">
        <div className="px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="mobile-menu-button"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Link to="/dashboard" className="flex items-center space-x-2">
            <img src="/onxflow_logo.png" alt="OnxFlow Logo" className="w-8 h-8" />
            <span className="font-bold text-gray-900">OnxFlow</span>
          </Link>
          {/* Boşluk - Bildirim ikonu sağ üstte fixed olarak zaten var */}
          <div className="w-10"></div>
        </div>
      </header>

      {/* Sidebar - Desktop & Mobile */}
      <aside 
        className={`fixed left-0 top-0 h-full w-64 glass border-r border-gray-200 z-30 transition-transform duration-300 flex flex-col ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } lg:translate-x-0`} 
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className="p-6 flex-shrink-0">
          <Link to="/dashboard" onClick={() => setSidebarOpen(false)} className="flex items-center space-x-3">
            <img src="/onxflow_logo.png" alt="OnxFlow Logo" className="w-10 h-10" />
            <h1 className="text-xl font-bold text-gray-900">OnxFlow</h1>
          </Link>
        </div>
        
        {/* Navigation Menu - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 pb-4">
          <nav className="space-y-2">
            {visibleMenuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={`flex items-center space-x-3 px-4 py-3 rounded-xl transition-all ${
                  location.pathname === item.path
                    ? 'bg-gradient-to-r from-cyan-500 to-blue-500 text-white shadow-lg'
                    : 'text-gray-700 hover:bg-white hover:shadow-md'
                }`}
              >
                {item.iconComponent === 'workflow' ? (
                  <Workflow className="w-5 h-5" />
                ) : item.iconComponent === 'barchart3' ? (
                  <BarChart3 className="w-5 h-5" />
                ) : item.iconComponent === 'layers' ? (
                  <Layers className="w-5 h-5" />
                ) : item.iconComponent === 'filetext' ? (
                  <FileText className="w-5 h-5" />
                ) : item.iconComponent === 'send' ? (
                  <Send className="w-5 h-5" />
                ) : item.iconComponent === 'scrolltext' ? (
                  <ScrollText className="w-5 h-5" />
                ) : item.iconComponent === 'shieldcheck' ? (
                  <ShieldCheck className="w-5 h-5" />
                ) : item.iconComponent === 'trophy' ? (
                  <Trophy className="w-5 h-5" />
                ) : item.iconComponent === 'bell' ? (
                  <Bell className="w-5 h-5" />
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                  </svg>
                )}
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>
        </div>
        
        {/* User Profile Section - Fixed at Bottom */}
        <div className="flex-shrink-0 p-6 border-t border-gray-200 bg-white/50 backdrop-blur-sm">
          <div className="flex items-center space-x-3 mb-4">
            {user?.picture && (
              <img 
                src={user.picture.startsWith('http') ? user.picture : `${BACKEND_URL}${user.picture}`}
                alt={user.name} 
                className="w-10 h-10 rounded-full object-cover" 
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-900 truncate">{user?.name}</p>
              <p className="text-xs text-gray-500">{getRoleDisplayName(user?.role)}</p>
            </div>
          </div>
          {/* Kullanıcının kazandığı rozetler */}
          <SidebarBadges />
          <Link
            to="/profile"
            onClick={() => setSidebarOpen(false)}
            className="w-full py-2 mb-2 text-sm font-medium text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Profil Ayarları
          </Link>
          <Link
            to="/notifications/settings"
            onClick={() => setSidebarOpen(false)}
            className="w-full py-2 mb-2 text-sm font-medium text-cyan-600 hover:bg-cyan-50 rounded-lg transition-colors flex items-center"
          >
            <Send className="w-4 h-4 mr-2" />
            Bildirim Ayarları
          </Link>
          <button
            onClick={() => {
              setSidebarOpen(false);
              handleLogout();
            }}
            data-testid="logout-button"
            className="w-full py-2 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors flex items-center"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
            </svg>
            Çıkış Yap
          </button>
        </div>
      </aside>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && !showNotifications && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Sticky Active Tasks Banner */}
        <ActiveTasksBanner />
        {/* Login sonrası karşılama modalı */}
        <ActiveTasksWelcomeModal />
        {/* Page Content */}
        <main data-testid="main-content">
          {children}
        </main>
      </div>
    </div>
    </NotificationsContext.Provider>
  );
}
